import React, { useRef, useState, useEffect } from "react";
import BreadCrum from "../../components/common/BreadCrum";
import {
  CLOCK_VECTOR,
} from "../../utils/aap-image-constant";
import TableLayout from "../../components/layouts/table-layout";
import Button from "../../components/common/button";
import { useTranslation } from "react-i18next";
import {
  doctorListforAvailability,
  myPaymentList,
} from "../../hooks/services/api-services";
import { useSelector } from "react-redux";
import { decryptAEStoJSON, EllipsisWithTooltip, toastEmitter } from "../../utils/utilities";
import { API_RESPONSE, DEFAULT_PAGE_LENGTH } from "../../utils/app-constants";
import AccessPermissionpage from "../../components/common/accessPermissionpage";
import moment from "moment";
import DoctorInvoice from "./doctor-invoice";
import { getCurrencySymbol } from "../../utils/form-utils";

const Payment = () => {
  const { t, i18n } = useTranslation();
  const hasShownErrorToast = useRef(false);
  const isFirstRender = useRef(true);
  const [pending, setPending] = useState(false);
  const [page, setPage] = useState(0);
  const [totalPayment, setTotalPayment] = useState(0);

  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_LENGTH);
  const [sortColumn, setSortColumn] = useState("");
  const [sortDirection, setSortDirection] = useState("");
  const [totalRows, setTotalRows] = useState(0);
  const [rowData, setRowData] = useState([]);
  const [doctorList, setDoctorList] = useState([]);
  const [selectedDoctorId, setSelectedDoctorId] = useState("");
  const todayDate = new Date()?.toISOString()?.split("T")[0];
  const { value } = useSelector((state) => state?.loggedUser);
  const userData = decryptAEStoJSON(value);
  const currencySign = getCurrencySymbol(userData?.country);
  const [activeTab, setActiveTab] = useState("payment");

  const fetchDoctorList = async () => {
    const doctorPayload = {
      pageIndex: 0,
      pageSize: 0,
      status: null,
      country: userData?.country,
      clinicId: userData?.id,
      searchText: "",
      direction: "desc",
      filterByFieldName: "id",
      bothDoctors: false,
      type: "CLINIC",
      languageType: i18n.language
    };
    try {
      const response = await doctorListforAvailability(doctorPayload);
      if (response?.data?.status === 200) {
        const sortedDoctors = (response?.data?.data?.doctorResponseList || []).sort((a, b) => {
          const nameA = `${a.firstName || ''} ${a.lastName || ''}`;
          const nameB = `${b.firstName || ''} ${b.lastName || ''}`;
          return nameA.localeCompare(nameB);
        });
        setDoctorList(sortedDoctors);
      } else {
        setDoctorList([]);
      }
    } catch (err) {
      setDoctorList([]);
    }
  };
  useEffect(() => {
    fetchDoctorList();
  }, []);

  const [selectedFormDate, setSelectedFormDate] = useState("")
  const [selectedToDate, setSelectedToDate] = useState("")
  const handleDateChange = (e) => {
    const { name, value } = e.target;

    if (value?.length > 0) {
      if (name === "fromDate") {
        setSelectedFormDate(value);

        if (selectedToDate && selectedToDate < value) {
          setSelectedToDate(""); // or setSelectedToDate(value) to match it
        }
      } else if (name === "toDate") {
        // Only set if toDate >= fromDate
        if (selectedFormDate && value < selectedFormDate) {
          // Optional: Show validation error or toast here
          return;
        }
        setSelectedToDate(value);
      }
    } else {
      if (name === "fromDate") setSelectedFormDate("");
      if (name === "toDate") setSelectedToDate("");
    }
  };

  const paymentpayload = {
    pageIndex: page || 0,
    pageSize: pageSize || DEFAULT_PAGE_LENGTH,
    searchText: "",
    direction: sortDirection || "desc",
    filterByFieldName: sortColumn || "id",
    doctorId: Number(selectedDoctorId) || 0,
    patientId: 0,
    clinicId: userData?.id,
    userType: ["CLINIC"],
    fromDate: selectedFormDate,
    toDate: selectedToDate,
    languageType: i18n.language
  };


  const fetchPaymentList = async () => {
    setPending(true);
    try {
      const response = await myPaymentList(paymentpayload);
      if (response?.data?.status !== 200) {
        setRowData([]);
      } else {
        setRowData(response?.data?.data?.transactionMasterResponseList);
        setTotalPayment(response?.data?.data?.total || 0);

        setTotalRows(response.data?.data?.totalElement);
        hasShownErrorToast.current = false;
      }
    } catch (err) {
      if (!isFirstRender.current && !hasShownErrorToast.current) {
        toastEmitter("error", API_RESPONSE?.MESSAGE_503);
        hasShownErrorToast.current = true;
      }
    } finally {
      setPending(false);
      isFirstRender.current = false;
    }
  };

  useEffect(() => {
    if (userData?.id && activeTab === "payment") {
      fetchPaymentList();
    }
  }, [page, pageSize, userData?.id, sortColumn, sortDirection, i18n.language, activeTab]);

  const tableColumns = [
    {
      name: t("S.No"),
      width: "80px",
      selector: (row, index) => `${page * pageSize + index + 1}`,
    },
    {
      name: t("Appointment ID"),
      selector: (row) => row.bookingId,
      sortable: true,
      width: "155px",
      cell: (row) => (
        <div className="d-flex gap-2 align-items-center fw-500 fs-12">
          {row.bookingId}
        </div>
      ),
    },
    {
      name: t("Patient Name"),
      code: "patientName",
      sortable: true,
      width: "180px",
      // selector: (row) => row.patientName,
      cell: (row) => (
        <EllipsisWithTooltip
          text={`${row?.patientName || "N/A"}`}
          maxWidth="250px"
        />
        // <div className="d-flex gap-2 align-items-center">
        //   {row.patientName}
        // </div>
      ),
    },
    {
      name: t("Doctor Name"),
      code: "doctorName",
      sortable: true,
      width: "180px",
      // selector: (row) => row.doctorName,
      cell: (row) => (
        <EllipsisWithTooltip
          text={`${row?.doctorName || "N/A"}`}
          maxWidth="250px"
        />
        // <div className="d-flex gap-2 align-items-center">
        //   {row.doctorName}
        // </div>
      ),
    },
    {
      name: t("Transaction Date"),
      width: "170px",
      code: "transactionDate",
      sortable: true,
      selector: (row) => (
        <div className="d-flex gap-2 align-items-center" >
          {
            moment(row?.transactionDate, "DD-MM-YYYY").isValid()
              ? moment(row?.transactionDate, "DD-MM-YYYY").format("DD MMM YYYY")
              : "Invalid date"
          }
        </div >
      ),
    },

    {
      id: "paymentFees",
      name: t("Admin Share"),
      width: "130px",
      sortable: false,
      cell: (row) => (
        <div>
          {row?.fees === null ? (
            <div className="cursor-pointer bg-green-normal rounded p-2 ">
              <span className="fw-600  color-green-bold">Fees</span>
            </div>
          ) : (
            <div>
              <span className="bg-success bg-opacity-10 text-success fw-bold px-3 py-2 rounded">
                {currencySign}
                {row?.adminShare}
              </span>
            </div>
          )}
        </div>
      ),
    },
    {
      id: "clinicShare",
      name: t("Clinic Share"),
      width: "120px",
      sortable: false,
      cell: (row) => (
        <div>
          {row?.fees === null ? (
            <div className="cursor-pointer bg-green-normal rounded p-2 ">
              <span className="fw-600  color-green-bold">Fees</span>
            </div>
          ) : (
            <div>
              <span className="bg-success bg-opacity-10 text-success fw-bold px-3 py-2 rounded">
                {currencySign}
                {row?.clinicShare}
              </span>
            </div>
          )}
        </div>
      ),
    },

    {
      id: "remark",
      name: t("Remark"),
      width: "250px",
      sortable: false,
      cell: (row) => (
        <div style={{ whiteSpace: "normal", wordBreak: "break-word", maxWidth: "240px" }} title={row.remarks}>
          {row?.fees === null ? (
            <div className="cursor-pointer bg-green-normal rounded p-2">
              <span className="fw-600 color-green-bold">Fees</span>
            </div>
          ) : (
            <div className=" cursor-pointer">
              {row?.remarks}
            </div>
          )}
        </div>
      ),
    }
    ,
    {
      name: t("Status"),
      selector: (row) => row.status,
      width: "120px",
      cell: (row) => {
        let statusLabel = "";
        let bgColor = "";
        let textColor = "";
        switch (row?.paymentStatus && row?.paymentStatus.toLowerCase()) {
          case 'success':
            statusLabel = "Success";
            bgColor = "bg-green-normal";
            textColor = "color-green-bold";
            break;
          case 'pending':
            statusLabel = "Pending";
            bgColor = "bg-orange-normal";
            textColor = "color-orange-bold";
            break;
          case 3:
            statusLabel = "Cancelled";
            bgColor = "bg-gray-normal";
            textColor = "color-gray-bold";
            break;
          default:
            statusLabel = "Inactive";
            bgColor = "bg-gray-normal";
            textColor = "color-gray-bold";
        }

        return (
          <div
            className={`${bgColor} ${textColor} d-flex align-items-center gap-1 px-2 py-1 rounded-4`}
          >

            {/* <p className="m-0 fs-12 fw-normal">{row?.paymentStatus}</p> */}
            <p className="m-0 fs-12 fw-normal">{statusLabel}</p>
          </div>
        );
      },
    },
  ];
  const handleSearch = () => {
    fetchPaymentList();
  };

  // permission code for this module start
  const [permissionAccess, setPermissionAccess] = useState();
  const rawPermission = useSelector((state) => state?.permission?.value);
  const permissions = decryptAEStoJSON(rawPermission);
  useEffect(() => {
    const RoleAccessFilterData =
      permissions &&
      permissions?.filter((v) => v?.moduleName === "Payment Management");
    setPermissionAccess(RoleAccessFilterData?.[0]);
  }, []);
  // permission code for this module end
  return (
    <>
      {permissionAccess?.addAction === 0 &&
        permissionAccess?.updateAction === 0 &&
        permissionAccess?.viewAction === 0 ? (
        <>
          <AccessPermissionpage />
        </>
      ) : (
        <div className="main_datatable my-3  bank_tabs">
          <BreadCrum firstData={t("Payment Management")} className="fw-600 fs-20 " />
          <ul className="nav nav-pills gap-2" id="pills-tab" role="tablist">
            {/* <li className="nav-item" role="presentation ">
              <button
                className="nav-link active mb-2"
                id="pills-home-tab"
                data-bs-toggle="pill"
                data-bs-target="#pills-home"
                type="button"
                role="tab"
                aria-controls="pills-home"
                aria-selected="true"
              >
                {t("My Payment")}
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link  ms-sm-2 ms-0 mb-2 ${i18n.language === "ar" ? "me-0 me-sm-2" : ""
                  }`}
                id="pills-profile-tab"
                data-bs-toggle="pill"
                data-bs-target="#pills-profile"
                type="button"
                role="tab"
                aria-controls="pills-profile"
                aria-selected="false"
              >
                {t("Doctor Invoice")}
              </button>
            </li> */}
            <button
              className={`nav-link ${activeTab === "payment" ? "active" : ""}`}
              onClick={() => setActiveTab("payment")}
            >
              {t("My Payment")}
            </button>

            <button
              className={`nav-link ${activeTab === "invoice" ? "active" : ""}`}
              onClick={() => setActiveTab("invoice")}
            >
              {t("Doctor Invoice")}
            </button>


          </ul>
          <div className="tab-content" id="pills-tabContent">
            {activeTab === "payment" && (

              <div
                className="tab-pane fade show active"
                id="pills-home"
                role="tabpanel"
                aria-labelledby="pills-home-tab"
              >
                <div className="row g-3 mb-3 align-items-end">
                  <div className=" col-12 col-md-6 col-lg-4 ">
                    <div className="dropdown-container">
                      <label>{t("Select Doctor")}</label>
                      <select
                        className="form-select"
                        value={selectedDoctorId}
                        onChange={(e) => setSelectedDoctorId(e.target.value)}
                      >
                        <option value="">{t("Select")}</option>
                        {doctorList &&
                          doctorList?.length > 0 &&
                          doctorList?.map((option) => (
                            <option key={option.id} value={option.id}>
                              {option.firstName?.toLowerCase()} {option.lastName?.toLowerCase()}
                            </option>
                          ))}
                      </select>
                    </div>
                  </div>
                  <div className="col-12 col-md-6 col-lg-8">
                    <div className="row g-2 justify-content-md-end">
                      <div className="col-12 col-sm-6 col-md-4">
                        <label className="form-label fw-semibold">{t("From Date")}</label>
                        <div className={`input-group  ${i18n.language === "ar" ? "flex-row-reverse" : "flex-row"
                          }`}>
                          <span className="input-group-text bg-white">
                            <img src={CLOCK_VECTOR} alt="calendar_icon" />
                          </span>
                          <input
                            type="date"
                            className="form-control py-2"
                            name="fromDate"
                            max={todayDate}
                            value={selectedFormDate}
                            onChange={handleDateChange}
                          />
                        </div>
                      </div>
                      <div className="col-12 col-sm-6 col-md-4 ">
                        <label className="form-label fw-semibold">{t("To Date")}</label>
                        <div className={`input-group  ${i18n.language === "ar" ? "flex-row-reverse" : "flex-row"
                          }`}>
                          <span className="input-group-text bg-white">
                            <img src={CLOCK_VECTOR} alt="calendar_icon" />
                          </span>
                          <input
                            type="date"
                            className="form-control py-2"
                            name="toDate"
                            max={todayDate}
                            min={selectedFormDate}
                            value={selectedToDate}
                            onChange={handleDateChange}
                          />
                        </div>
                      </div>
                      <div className="col-12 col-md-4 col-lg-3 d-flex align-items-end">
                        <Button
                          label={t("Search Doctor")}
                          type="button"
                          className="btn btn-primary w-100 py-2  rounded-3 text-white fw-500"
                          onClick={handleSearch}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="table-wrapper">
                  <TableLayout
                    _tblColumns={tableColumns}
                    _rowData={rowData}
                    pending={pending}
                    pagination={true}
                    selectableRows={false}
                    setPending={setPending}
                    _totalRows={totalRows}
                    pageSize={pageSize}
                    setPageSize={setPageSize}
                    setPage={setPage}
                    setSortColumn={setSortColumn}
                    setSortDirection={setSortDirection}
                  />
                  <div className="row ">
                    <div className="col-12 col-md-6 my-3">
                      <h5 className="d-flex justify-content-start text-align-center align-items-center fw-600">
                        {t("Total")}
                      </h5>
                    </div>
                    <div className="col-12 col-md-6 d-flex justify-content-md-end align-items-center gap-3 flex-wrap my-3">
                      <span className="bg-success bg-opacity-10 text-success fw-600 px-4 py-2 rounded">
                        {currencySign} {totalPayment?.toLocaleString()}
                      </span>

                      <Button
                        label={t("Export Invoice")}
                        type="button"
                        className="btn btn-primary px-4 py-2 border-0 rounded-3 text-white fw-500"
                      />
                    </div>
                  </div>
                </div>
              </div>)}
            {activeTab === "invoice" && (

              <div
                className="tab-pane show active"
                id="pills-profile"
                role="tabpanel"
                aria-labelledby="pills-profile-tab"
              >
                <DoctorInvoice key={activeTab} />
              </div>)}
          </div>
        </div>
      )}
    </>
  );
};

export default Payment;
